import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { pillarsApi, articlesApi, workspacesApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Spinner, Alert, StatCard } from '@rankhelm/ui';
import type { Workspace, Pillar, Article, PillarResearchStatus, PillarLifecycleStatus } from '@rankhelm/shared';

/** Parsed research JSON structure */
interface PillarResearch {
  overview?: string;
  rationale?: string;
  head_terms?: { keyword: string; priority: string }[];
  mid_tail_keywords?: { keyword: string; priority: string }[];
  long_tail_questions?: { keyword: string; priority: string }[];
  audience_intent_mapping?: { intent_type: string; user_need: string; content_approach: string }[];
  competitive_gaps?: string[];
  sub_topics?: { title: string; content_type: string; target_keyword: string; search_intent: string; estimated_word_count: number }[];
}

const STATUS_COLORS: Record<string, string> = {
  queued: 'bg-slate-500',
  generating: 'bg-yellow-500 animate-pulse',
  draft: 'bg-blue-500',
  in_review: 'bg-orange-500',
  revision_requested: 'bg-red-400',
  approved: 'bg-green-500',
  scheduled: 'bg-cyan-500',
  publishing: 'bg-yellow-500 animate-pulse',
  published: 'bg-emerald-500',
  publish_failed: 'bg-red-600',
};

const BADGE_COLORS: Record<string, string> = {
  draft: 'bg-yellow-500/20 text-yellow-400',
  approved: 'bg-blue-500/20 text-blue-400',
  locked: 'bg-green-500/20 text-green-400',
};

const LIFECYCLE_COLORS: Record<string, string> = {
  active: 'bg-green-500/20 text-green-400',
  paused: 'bg-yellow-500/20 text-yellow-400',
  archived: 'bg-slate-500/20 text-slate-400',
};

export function PillarDetailPage() {
  const { workspaceId, pillarId } = useParams();
  const { user } = useAuth();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [pillar, setPillar] = useState<Pillar | null>(null);
  const [pillarArticles, setPillarArticles] = useState<Article[]>([]);
  const [research, setResearch] = useState<PillarResearch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  useEffect(() => {
    loadData();
  }, [pillarId, workspaceId]);

  const loadData = async () => {
    try {
      const [wsRes, pillarRes, articlesRes] = await Promise.all([
        workspacesApi.get(workspaceId!),
        pillarsApi.get(pillarId!),
        articlesApi.list(workspaceId!, { pillar_id: pillarId }),
      ]);
      setWorkspace(wsRes.data);
      setPillar(pillarRes.data);
      setPillarArticles(articlesRes.data || []);

      // Parse research JSON
      if (pillarRes.data?.research_json) {
        try {
          setResearch(JSON.parse(pillarRes.data.research_json));
        } catch {
          setResearch(null);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pillar');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status: PillarResearchStatus) => {
    setActionLoading(status);
    setError('');
    try {
      await pillarsApi.update(pillarId!, { research_status: status });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setActionLoading('');
    }
  };

  const handleLifecycleChange = async (status: PillarLifecycleStatus) => {
    setActionLoading(`lifecycle-${status}`);
    setError('');
    try {
      await pillarsApi.update(pillarId!, { lifecycle_status: status });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lifecycle');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return <Spinner message="Loading pillar..." fullPage />;
  }

  if (!pillar || !workspace) {
    return <div className="text-red-400">Pillar not found</div>;
  }

  const isAdmin = user?.role === 'super_admin' || user?.role === 'account_admin';

  return (
    <div className="max-w-5xl mx-auto">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{pillar.name}</h1>
          {pillar.description && (
            <p className="text-slate-400 text-sm mt-1">{pillar.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${LIFECYCLE_COLORS[pillar.lifecycle_status] || ''}`}>
            {pillar.lifecycle_status}
          </span>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${BADGE_COLORS[pillar.research_status] || ''}`}>
            research: {pillar.research_status}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      {isAdmin && (
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-400 mr-2">Research:</span>
            {pillar.research_status === 'draft' && research && (
              <button
                onClick={() => handleStatusChange('approved')}
                disabled={!!actionLoading}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg transition-colors disabled:opacity-50"
              >
                {actionLoading === 'approved' ? 'Approving...' : 'Approve Research'}
              </button>
            )}
            {pillar.research_status === 'approved' && (
              <>
                <button
                  onClick={() => handleStatusChange('draft')}
                  disabled={!!actionLoading}
                  className="px-3 py-1.5 bg-navy-800 hover:bg-navy-700 text-slate-300 text-xs rounded-lg border border-navy-600 transition-colors disabled:opacity-50"
                >
                  Back to Draft
                </button>
                <button
                  onClick={() => handleStatusChange('locked')}
                  disabled={!!actionLoading}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'locked' ? 'Locking...' : 'Lock & Generate Articles'}
                </button>
              </>
            )}

            <div className="flex-1" />

            <span className="text-xs text-slate-400 mr-2">Lifecycle:</span>
            {pillar.lifecycle_status === 'active' && (
              <button
                onClick={() => handleLifecycleChange('paused')}
                disabled={!!actionLoading}
                className="px-3 py-1.5 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 text-xs rounded-lg transition-colors disabled:opacity-50"
              >
                Pause
              </button>
            )}
            {pillar.lifecycle_status === 'paused' && (
              <>
                <button
                  onClick={() => handleLifecycleChange('active')}
                  disabled={!!actionLoading}
                  className="px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 text-green-400 text-xs rounded-lg transition-colors disabled:opacity-50"
                >
                  Resume
                </button>
                <button
                  onClick={() => handleLifecycleChange('archived')}
                  disabled={!!actionLoading}
                  className="px-3 py-1.5 bg-slate-600/20 hover:bg-slate-600/30 text-slate-400 text-xs rounded-lg transition-colors disabled:opacity-50"
                >
                  Archive
                </button>
              </>
            )}
            {pillar.lifecycle_status === 'archived' && (
              <button
                onClick={() => handleLifecycleChange('paused')}
                disabled={!!actionLoading}
                className="px-3 py-1.5 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 text-xs rounded-lg transition-colors disabled:opacity-50"
              >
                Unarchive
              </button>
            )}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Articles" value={pillarArticles.length} />
        <StatCard label="Drafts" value={pillarArticles.filter(a => a.status === 'draft').length} />
        <StatCard label="In Review" value={pillarArticles.filter(a => a.status === 'in_review').length} />
        <StatCard label="Published" value={pillarArticles.filter(a => a.status === 'published').length} />
      </div>

      {/* Research Data */}
      {research && (
        <div className="space-y-6 mb-8">
          <h2 className="text-lg font-semibold text-white">Pillar Research</h2>

          {research.overview && (
            <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
              <h3 className="text-sm font-medium text-slate-300 mb-2">Overview</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{research.overview}</p>
            </div>
          )}

          {research.rationale && (
            <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
              <h3 className="text-sm font-medium text-slate-300 mb-2">Rationale</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{research.rationale}</p>
            </div>
          )}

          {/* Keywords */}
          {((research.head_terms?.length ?? 0) > 0 || (research.mid_tail_keywords?.length ?? 0) > 0 || (research.long_tail_questions?.length ?? 0) > 0) && (
            <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
              <h3 className="text-sm font-medium text-slate-300 mb-4">Keyword Map</h3>
              {research.head_terms && research.head_terms.length > 0 && (
                <KeywordSection label="Head Terms" keywords={research.head_terms} />
              )}
              {research.mid_tail_keywords && research.mid_tail_keywords.length > 0 && (
                <KeywordSection label="Mid-Tail Keywords" keywords={research.mid_tail_keywords} />
              )}
              {research.long_tail_questions && research.long_tail_questions.length > 0 && (
                <KeywordSection label="Long-Tail Questions" keywords={research.long_tail_questions} />
              )}
            </div>
          )}

          {/* Audience Intent */}
          {research.audience_intent_mapping && research.audience_intent_mapping.length > 0 ? (
            <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
              <h3 className="text-sm font-medium text-slate-300 mb-3">Audience Intent Mapping</h3>
              <div className="space-y-2">
                {research.audience_intent_mapping.map((intent, i: number) => (
                  <div key={i} className="bg-navy-800/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-helm-500/20 text-helm-400 rounded text-xs capitalize">
                        {intent.intent_type}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      <span className="text-slate-300">Need:</span> {intent.user_need}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      <span className="text-slate-300">Approach:</span> {intent.content_approach}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Competitive Gaps */}
          {research.competitive_gaps && research.competitive_gaps.length > 0 && (
            <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
              <h3 className="text-sm font-medium text-slate-300 mb-3">Competitive Gaps</h3>
              <div className="space-y-1">
                {research.competitive_gaps.map((gap: string, i: number) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-400">
                    <span className="text-helm-400 shrink-0 mt-0.5">&#9656;</span>
                    <span>{gap}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sub-Topics Preview */}
          {research.sub_topics && research.sub_topics.length > 0 ? (
            <div className="bg-navy-900 border border-navy-700 rounded-xl p-6">
              <h3 className="text-sm font-medium text-slate-300 mb-3">
                Suggested Sub-Topics ({research.sub_topics.length})
              </h3>
              <div className="space-y-2">
                {research.sub_topics.map((topic, i: number) => (
                  <div key={i} className="bg-navy-800/50 rounded-lg p-3">
                    <div className="flex items-start justify-between mb-1">
                      <span className="text-sm text-white font-medium">{topic.title}</span>
                      <span className="text-xs text-slate-500 shrink-0 ml-2">{topic.content_type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="text-helm-400">{topic.target_keyword}</span>
                      <span>·</span>
                      <span className="capitalize">{topic.search_intent}</span>
                      <span>·</span>
                      <span>~{topic.estimated_word_count} words</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {!research && (
        <div className="bg-navy-900/50 border border-navy-700 border-dashed rounded-xl p-8 text-center mb-8">
          <p className="text-slate-400 text-sm">No research data available yet.</p>
          <p className="text-slate-500 text-xs mt-1">Research is generated when pillars are auto-created from a locked intelligence report.</p>
        </div>
      )}

      {/* Articles List */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Articles ({pillarArticles.length})</h2>
        {pillarArticles.length === 0 ? (
          <div className="bg-navy-900/50 border border-navy-700 border-dashed rounded-xl p-8 text-center">
            <p className="text-slate-400 text-sm">
              {pillar.research_status === 'locked'
                ? 'Articles will appear here after generation.'
                : 'Lock the pillar research to auto-generate article topics.'}
            </p>
          </div>
        ) : (
          <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy-700">
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Title</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Keyword</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {pillarArticles.map((article) => (
                  <tr key={article.id} className="border-b border-navy-700/50 hover:bg-navy-800/50 transition-colors">
                    <td className="px-5 py-3">
                      <Link
                        to={`/workspaces/${workspaceId}/articles/${article.id}`}
                        className="text-white hover:text-helm-400 text-sm font-medium transition-colors"
                      >
                        {article.title}
                      </Link>
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[article.status] || 'bg-slate-500'}`} />
                        <span className="text-sm text-slate-300">{article.status.replace(/_/g, ' ')}</span>
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-slate-400 text-sm">{article.target_keyword || '—'}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-slate-500 text-xs">{new Date(article.created_at).toLocaleDateString()}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KeywordSection({ label, keywords }: { label: string; keywords: { keyword: string; priority: string }[] }) {
  return (
    <div className="mb-4">
      <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">{label}</h4>
      <div className="flex flex-wrap gap-2">
        {keywords.map((kw, i: number) => (
          <span key={i} className="px-2 py-1 bg-navy-800 rounded text-xs text-slate-300 border border-navy-700">
            {kw.keyword}
            <span className={`ml-1.5 ${
              kw.priority === 'high' ? 'text-red-400' :
              kw.priority === 'medium' ? 'text-yellow-400' :
              'text-green-400'
            }`}>
              {kw.priority === 'high' ? '!!!' : kw.priority === 'medium' ? '!!' : '!'}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
